/**
 * report-due-money (US-E23.F1-0847 · @UC9514 @UC9241) — **phép cộng tiền của BÁO CÁO PDF** trên một
 * `member_dues`: phải thu / đã thu / còn thiếu.
 *
 * ═══ VÌ SAO FILE NÀY Ở `packages/domain`, KHÔNG CHÉP SANG WORKER ═══
 * §2 và §3 của bản PDF mới cộng tiền từ `member_dues`, mà `apps/worker` **không** dep
 * `@goppy/contracts`. Luật gốc nằm ở `apps/api/src/modules/collections/collection-progress.math.ts`,
 * nhưng file đó import `@goppy/contracts` và chiều phụ thuộc là **contracts → domain** ⇒ mang nó
 * xuống đây là **vòng tròn cấp package** (đo 23/09/2026: thử `git mv` ⇒ guard
 * `collection-progress.math.invariants.spec.ts` **BIẾN MẤT** — `Tests no tests` — thay vì đỏ, đúng
 * lớp xanh-giả *"exit 0 ≠ test đã chạy"*).
 *
 * ⇒ **ĐƯỜNG C (QĐ người dùng 23/09/2026): không move, không mirror literal status.** File này dựng
 * trên các hằng ĐÃ CÓ của `member-due-status.ts` — cùng nguồn mà `apps/api` dùng — nên không có
 * whitelist status thứ hai nào ra đời. `collection-progress.math.ts` **giữ nguyên tại chỗ**.
 *
 * 🔴 ĐÂY KHÔNG PHẢI "ENGINE THU THỨ HAI" (CLAUDE.md §3.8). Nó không đọc DB, không quyết trạng thái,
 * không ghi gì; nó chỉ trả lời *"in con số nào lên giấy"* bằng đúng các bucket mà repo đã chốt. Mọi
 * quyết định về ngữ nghĩa status vẫn thuộc `member-due-status.ts`.
 *
 * 🔴 TIỀN LÀ BigInt MINOR, KHÔNG FLOAT Ở BẤT KỲ BƯỚC NÀO (§3.1).
 */

import { remainingMinorOf } from "./collection-payment.js";
import {
  AWAITING_CONFIRMATION_DUE_STATUS,
  CONTRIBUTION_COUNTED_STATUSES,
  MEMBER_DUE_WAIVED_STATUS,
  PARTIALLY_PAID_DUE_STATUS,
} from "./member-due-status.js";

/**
 * Trạng thái tính là **ĐÃ THU** trên báo cáo = `paid` + `paid_on_behalf`.
 *
 * ═══ 🔴 VÌ SAO CÓ HẰNG RIÊNG THAY VÌ DÙNG THẲNG `CONTRIBUTION_COUNTED_STATUSES` ═══
 * Hôm nay hai tập **TRÙNG NHAU HOÀN TOÀN**, nên mượn thì mọi test vẫn xanh — và đó chính là lý do
 * phải viết chỗ này ra thành lời.
 *
 * `CONTRIBUTION_COUNTED_STATUSES` trả lời *"khoản này đã XONG chưa"* và là **một chân của một bất
 * biến phân hoạch** (`member-due-status.ts`: COUNTED ∪ OUTSTANDING ∪ {waived} == tất cả, ba tập rời
 * nhau). Tập này trả lời một câu khác: *"in bao nhiêu tiền vào cột ĐÃ THU"*. Ngày nào đó phân hoạch
 * cần đổi vì lý do của chính nó — thêm một status cuối đời, gộp một nhánh — thì con số **in trên
 * giấy cho cả nhóm phụ huynh đọc** sẽ đổi theo mà **không phép đo nào đỏ**, vì hằng bị mượn vẫn
 * "đúng" theo nghĩa của nó. Hai câu hỏi, hai hằng (cùng lý lẽ `PAYMENT_APPROVER_ROLE_NAMES`).
 *
 * 🔴 `waived` KHÔNG ở đây và cũng KHÔNG ở vế còn thiếu — xem [reportExpectedMinorOf]. Miễn ⇒ nhóm
 * không kỳ vọng thu, nên đưa nó vào "đã thu" là **bịa ra một khoản tiền chưa từng vào quỹ**.
 * `report-due-money.spec.ts` khoá vế này tường minh, không suy từ tập kia.
 */
export const REPORT_PAID_DUE_STATUSES: readonly string[] = Object.freeze([
  ...CONTRIBUTION_COUNTED_STATUSES,
]);

/** Hình dạng tối thiểu của một `member_dues` để trả lời ba câu hỏi tiền của báo cáo. */
export interface ReportDueShape {
  readonly status: string;
  readonly amountMinor: bigint;
  readonly paidAmountMinor: bigint;
}

/** Kẹp phần đã đóng vào `[0, amountMinor]` — dữ liệu lệch không được đẻ số âm hay vượt khoản. */
function clampPaid(d: ReportDueShape): bigint {
  if (d.paidAmountMinor <= 0n) return 0n;
  return d.paidAmountMinor > d.amountMinor ? d.amountMinor : d.paidAmountMinor;
}

/**
 * **PHẢI THU** của một khoản. `waived` ⇒ `0` (miễn = không kỳ vọng thu) — vế này giữ cho đẳng thức
 * của §2 (`Phải thu − Đã thu = Còn thiếu`) đúng mà không cần ngoại lệ ở tầng gọi.
 */
export function reportExpectedMinorOf(d: ReportDueShape): bigint {
  if (d.status === MEMBER_DUE_WAIVED_STATUS) return 0n;
  return d.amountMinor;
}

/**
 * **ĐÃ THU** của một khoản.
 *
 * 🔴 `paid`/`paid_on_behalf` lấy `amountMinor`, KHÔNG đọc `paidAmountMinor`: due cũ (trước khi cột
 * đó được dùng) mang `0`, đọc cột sẽ làm tổng đã thu **tụt xuống im lặng** trên dữ liệu đang chạy.
 *
 * 🔴 `awaiting_confirmation` cộng phần ĐÃ VÀO SỔ (`paidAmountMinor`), **không** cộng lời khai đang
 * chờ. §3.7 nguyên vẹn: (2) *"đã chuyển"* ≠ (3) *"đã nhận"*. Trả thẳng `0n` sẽ làm tiến độ tụt khi
 * một người bấm nút khai trên khoản đã đóng dở (vá review PR #679 HIGH-3).
 */
export function reportCollectedMinorOf(d: ReportDueShape): bigint {
  if (d.status === MEMBER_DUE_WAIVED_STATUS) return 0n;
  if (REPORT_PAID_DUE_STATUSES.includes(d.status)) return d.amountMinor;
  if (d.status === PARTIALLY_PAID_DUE_STATUS) return clampPaid(d);
  if (d.status === AWAITING_CONFIRMATION_DUE_STATUS) return clampPaid(d);
  return 0n;
}

/**
 * **CÒN THIẾU** của một khoản. Bất biến (khoá bằng test): với mọi status trừ `waived`,
 * `reportCollectedMinorOf + reportOutstandingMinorOf === reportExpectedMinorOf`.
 */
export function reportOutstandingMinorOf(d: ReportDueShape): bigint {
  if (d.status === MEMBER_DUE_WAIVED_STATUS) return 0n;
  if (REPORT_PAID_DUE_STATUSES.includes(d.status)) return 0n;
  if (d.status === PARTIALLY_PAID_DUE_STATUS || d.status === AWAITING_CONFIRMATION_DUE_STATUS) {
    // MỘT phép trừ "còn thiếu" cho api · worker · báo cáo (POB-CLOSED-LOWS) — cùng hàm với cửa ghi tiền vào.
    return remainingMinorOf(d.amountMinor, clampPaid(d));
  }
  return d.amountMinor;
}

